import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { onboardingApi, employeeApi } from '../../services/api';
import { EmptyState, Spinner, Modal, ConfirmModal } from '../../components/shared/index';
import {
  PlusIcon,
  TrashIcon,
  PencilSquareIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckCircleIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';

const MILESTONES = ['day_0','day_30','day_60','day_90','ongoing'];
const MILESTONE_LABELS = {
  day_0:   'Day 0',
  day_30:  'Day 30',
  day_60:  'Day 60',
  day_90:  'Day 90',
  ongoing: 'Ongoing',
};
const MILESTONE_STYLES = {
  day_0:   'bg-blue-100   text-blue-800',
  day_30:  'bg-purple-100 text-purple-800',
  day_60:  'bg-amber-100  text-amber-800',
  day_90:  'bg-green-100  text-green-800',
  ongoing: 'bg-gray-100   text-gray-800',
};

function MilestoneChip({ milestone }) {
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold', MILESTONE_STYLES[milestone] || MILESTONE_STYLES.ongoing)}>
      {MILESTONE_LABELS[milestone] || milestone}
    </span>
  );
}

function ProgressBar({ pct }) {
  return (
    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
      <div className={clsx('h-full rounded-full transition-all',
        pct === 100 ? 'bg-green-500' : pct >= 60 ? 'bg-blue-500' : 'bg-amber-400'
      )} style={{ width: `${pct}%` }} />
    </div>
  );
}

function PlanEditor({ open, onClose, plan, tasks, onSavePlan, onAddTask, onUpdateTask, onDeleteTask, savingPlan }) {
  const [name, setName] = useState(plan?.name || '');
  const [role, setRole] = useState(plan?.role || '');
  const [description, setDescription] = useState(plan?.description || '');

  useEffect(() => {
    setName(plan?.name || '');
    setRole(plan?.role || '');
    setDescription(plan?.description || '');
  }, [plan]);

  const [newTask, setNewTask] = useState({ title: '', milestone: 'day_0', is_required: true });

  return (
    <Modal open={open} onClose={onClose} title={plan ? `Edit plan: ${plan.name}` : 'New onboarding plan'} size="lg">
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Name</label>
            <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="Standard engineering onboarding" />
          </div>
          <div>
            <label className="label">Role (optional)</label>
            <input className="input" value={role} onChange={e => setRole(e.target.value)} placeholder="developer" />
          </div>
        </div>
        <div>
          <label className="label">Description</label>
          <textarea className="input h-20 resize-none" value={description} onChange={e => setDescription(e.target.value)} />
        </div>
        <div className="flex justify-end">
          <button
            disabled={!name || savingPlan}
            className="btn-primary disabled:opacity-40"
            onClick={() => onSavePlan({ name, role: role || null, description: description || null })}
          >
            {savingPlan ? 'Saving…' : (plan ? 'Save plan' : 'Create plan')}
          </button>
        </div>

        {plan && (
          <div className="border-t border-gray-100 pt-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="section-title">Tasks</h4>
              <span className="text-xs text-gray-500">{tasks?.length || 0} tasks</span>
            </div>

            <div className="space-y-2 mb-4">
              {(tasks || []).map(t => (
                <div key={t.id} className="flex items-start gap-2 card p-3">
                  <div className="flex-1 min-w-0">
                    <input
                      className="input mb-2 text-sm"
                      defaultValue={t.title}
                      onBlur={e => e.target.value !== t.title && onUpdateTask(t.id, { title: e.target.value })}
                    />
                    <div className="flex items-center gap-2 flex-wrap">
                      <select
                        className="input text-xs !py-1 w-32"
                        defaultValue={t.milestone}
                        onChange={e => onUpdateTask(t.id, { milestone: e.target.value })}
                      >
                        {MILESTONES.map(m => <option key={m} value={m}>{MILESTONE_LABELS[m]}</option>)}
                      </select>
                      <label className="inline-flex items-center gap-1 text-xs text-gray-600">
                        <input type="checkbox" defaultChecked={t.is_required}
                          onChange={e => onUpdateTask(t.id, { is_required: e.target.checked })} />
                        Required
                      </label>
                    </div>
                  </div>
                  <button onClick={() => onDeleteTask(t.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600 shrink-0">
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {(!tasks || tasks.length === 0) && <p className="text-sm text-gray-400 text-center py-4">No tasks yet.</p>}
            </div>

            <div className="card p-3 bg-gray-50">
              <div className="flex gap-2 flex-wrap">
                <input className="input text-sm flex-1 min-w-[160px]" placeholder="New task title…"
                  value={newTask.title} onChange={e => setNewTask(t => ({ ...t, title: e.target.value }))} />
                <select className="input text-sm w-32" value={newTask.milestone}
                  onChange={e => setNewTask(t => ({ ...t, milestone: e.target.value }))}>
                  {MILESTONES.map(m => <option key={m} value={m}>{MILESTONE_LABELS[m]}</option>)}
                </select>
                <button
                  disabled={!newTask.title}
                  className="btn-primary disabled:opacity-40"
                  onClick={() => {
                    onAddTask({ ...newTask });
                    setNewTask({ title: '', milestone: 'day_0', is_required: true });
                  }}
                >
                  <PlusIcon className="w-4 h-4" /> Add
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function AssignModal({ open, onClose, onSubmit, pending, plans, employees }) {
  const [empId, setEmpId] = useState('');
  const [planId, setPlanId] = useState('');
  const [target, setTarget] = useState('');

  return (
    <Modal open={open} onClose={() => { setEmpId(''); setPlanId(''); setTarget(''); onClose(); }} title="Assign onboarding plan" size="sm">
      <div className="space-y-4">
        <div>
          <label className="label">Employee</label>
          <select className="input" value={empId} onChange={e => setEmpId(e.target.value)}>
            <option value="">Select an employee…</option>
            {(employees || []).map(em => <option key={em.id} value={em.id}>{em.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Plan</label>
          <select className="input" value={planId} onChange={e => setPlanId(e.target.value)}>
            <option value="">Select a plan…</option>
            {(plans || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Target completion date</label>
          <input type="date" className="input" value={target} onChange={e => setTarget(e.target.value)} />
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={() => { setEmpId(''); setPlanId(''); setTarget(''); onClose(); }} className="btn-secondary">Cancel</button>
          <button disabled={!empId || !planId || pending}
            onClick={() => onSubmit({ empId, planId, target_completion_date: target || undefined })}
            className="btn-primary disabled:opacity-40">
            {pending ? 'Assigning…' : 'Assign'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ActiveRow({ employee }) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['onboarding-status', employee.id],
    queryFn:  () => onboardingApi.status(employee.id).then(r => r.data),
    enabled:  expanded,
  });

  const mark = useMutation({
    mutationFn: ({ taskId, assignmentId, done }) => done
      ? onboardingApi.markTask(taskId, { assignment_id: assignmentId })
      : onboardingApi.unmarkTask(taskId, { assignment_id: assignmentId }),
    onSuccess:  () => { toast.success('Updated'); qc.invalidateQueries({ queryKey: ['onboarding-status', employee.id] }); qc.invalidateQueries({ queryKey: ['onboarding-active'] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const firstAssignment = data?.assignments?.[0];

  return (
    <div className="card">
      <button
        onClick={() => setExpanded(x => !x)}
        className="w-full flex items-center gap-4 p-4 hover:bg-gray-50"
      >
        <div className="w-10 h-10 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-sm font-semibold shrink-0">
          {employee.name[0]}
        </div>
        <div className="flex-1 min-w-0 text-left">
          <Link to={`/app/employees/${employee.id}`} className="text-sm font-semibold text-gray-900 hover:text-blue-600">{employee.name}</Link>
          <p className="text-xs text-gray-500 capitalize">{employee.role?.replace('_',' ')} · hired {employee.hire_date ? new Date(employee.hire_date).toLocaleDateString() : '—'}</p>
        </div>
        <div className="w-48 shrink-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs text-gray-500">{firstAssignment?.progress ?? 0}%</span>
            <span className="text-xs text-gray-400">{firstAssignment ? `${firstAssignment.completed_tasks}/${firstAssignment.total_tasks}` : ''}</span>
          </div>
          <ProgressBar pct={firstAssignment?.progress ?? 0} />
        </div>
        {expanded ? <ChevronUpIcon className="w-4 h-4 text-gray-400" /> : <ChevronDownIcon className="w-4 h-4 text-gray-400" />}
      </button>

      {expanded && (
        <div className="border-t border-gray-100 p-4 space-y-4">
          {isLoading && <div className="flex justify-center py-6"><Spinner /></div>}
          {(data?.assignments || []).map(a => (
            <div key={a.assignment_id}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-semibold">{a.plan.name}</p>
                  <p className="text-xs text-gray-500">
                    {a.target_completion_date && `Target: ${new Date(a.target_completion_date).toLocaleDateString()}`}
                    {a.completed_at && <span className="ml-2 text-green-600">· Completed {new Date(a.completed_at).toLocaleDateString()}</span>}
                  </p>
                </div>
                <span className="text-sm font-bold text-gray-800">{a.progress}%</span>
              </div>

              {MILESTONES.map(m => {
                const tasks = a.tasks.filter(t => t.milestone === m);
                if (tasks.length === 0) return null;
                return (
                  <div key={m} className="mb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <MilestoneChip milestone={m} />
                      <span className="text-xs text-gray-400">{tasks.filter(t => t.completed).length}/{tasks.length}</span>
                    </div>
                    <ul className="space-y-1">
                      {tasks.map(t => (
                        <li key={t.id} className="flex items-start gap-2 py-1">
                          <input
                            type="checkbox"
                            checked={!!t.completed}
                            onChange={e => mark.mutate({ taskId: t.id, assignmentId: a.assignment_id, done: e.target.checked })}
                            className="mt-0.5"
                          />
                          <div className="flex-1 min-w-0">
                            <p className={clsx('text-sm', t.completed ? 'line-through text-gray-400' : 'text-gray-800')}>{t.title}</p>
                            {t.description && <p className="text-xs text-gray-500">{t.description}</p>}
                          </div>
                          {t.is_required && <span className="text-[10px] text-red-500 uppercase">Required</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          ))}
          {data && data.assignments.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-6">No onboarding plan assigned yet.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function OnboardingTab() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = ['hr_admin','super_admin'].includes(user?.role);
  const [innerTab, setInnerTab] = useState('active');
  const [editingPlan, setEditingPlan] = useState(null); // plan object or 'new'
  const [assignOpen, setAssignOpen] = useState(false);
  const [confirmDeletePlan, setConfirmDeletePlan] = useState(null);

  const { data: plans, isLoading: loadingPlans } = useQuery({
    queryKey: ['onboarding-plans'],
    queryFn:  () => onboardingApi.plans.list().then(r => r.data),
  });

  const { data: planDetail } = useQuery({
    queryKey: ['onboarding-plan', editingPlan?.id],
    queryFn:  () => onboardingApi.plans.get(editingPlan.id).then(r => r.data),
    enabled:  !!editingPlan?.id,
  });

  const { data: employees } = useQuery({
    queryKey: ['employees','all-onboarding'],
    queryFn:  () => employeeApi.list().then(r => {
      const arr = r.data?.data || r.data;
      return Array.isArray(arr) ? arr : [];
    }),
  });

  const recentHires = useMemo(() => {
    return (employees || [])
      .filter(e => e.status !== 'terminated' && e.hire_date)
      .sort((a, b) => new Date(b.hire_date) - new Date(a.hire_date))
      .slice(0, 30);
  }, [employees]);

  // Mutations
  const createPlan = useMutation({
    mutationFn: onboardingApi.plans.create,
    onSuccess:  (r) => { toast.success('Plan created'); qc.invalidateQueries({ queryKey: ['onboarding-plans'] }); setEditingPlan(r.data); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed'),
  });
  const updatePlan = useMutation({
    mutationFn: ({ id, data }) => onboardingApi.plans.update(id, data),
    onSuccess:  () => { toast.success('Plan saved'); qc.invalidateQueries({ queryKey: ['onboarding-plans'] }); qc.invalidateQueries({ queryKey: ['onboarding-plan'] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed'),
  });
  const destroyPlan = useMutation({
    mutationFn: (id) => onboardingApi.plans.destroy(id),
    onSuccess:  () => { toast.success('Plan archived'); qc.invalidateQueries({ queryKey: ['onboarding-plans'] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const addTask = useMutation({
    mutationFn: ({ planId, data }) => onboardingApi.plans.addTask(planId, data),
    onSuccess:  () => { toast.success('Task added'); qc.invalidateQueries({ queryKey: ['onboarding-plan'] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed'),
  });
  const updateTask = useMutation({
    mutationFn: ({ id, data }) => onboardingApi.plans.updateTask(id, data),
    onSuccess:  () => { toast.success('Saved'); qc.invalidateQueries({ queryKey: ['onboarding-plan'] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed'),
  });
  const deleteTask = useMutation({
    mutationFn: (id) => onboardingApi.plans.deleteTask(id),
    onSuccess:  () => { toast.success('Removed'); qc.invalidateQueries({ queryKey: ['onboarding-plan'] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const assign = useMutation({
    mutationFn: ({ empId, planId, target_completion_date }) =>
      onboardingApi.assign(empId, planId, target_completion_date ? { target_completion_date } : {}),
    onSuccess:  () => { toast.success('Plan assigned'); setAssignOpen(false); qc.invalidateQueries({ queryKey: ['onboarding-status'] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const innerTabs = [
    { key: 'active', label: 'Active onboardings' },
    ...(isAdmin ? [{ key: 'plans', label: 'Plans (templates)' }] : []),
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div className="flex gap-1 border-b border-gray-200 flex-1">
          {innerTabs.map(t => (
            <button key={t.key} onClick={() => setInnerTab(t.key)}
              className={clsx('px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                innerTab === t.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700')}>
              {t.label}
            </button>
          ))}
        </div>
        {innerTab === 'active' && isAdmin && (
          <button onClick={() => setAssignOpen(true)} className="btn-primary ml-4">
            <UserPlusIcon className="w-4 h-4" /> Assign plan
          </button>
        )}
        {innerTab === 'plans' && isAdmin && (
          <button onClick={() => setEditingPlan({})} className="btn-primary ml-4">
            <PlusIcon className="w-4 h-4" /> New plan
          </button>
        )}
      </div>

      {innerTab === 'active' && (
        <div className="space-y-3">
          {recentHires.length === 0
            ? <EmptyState title="No recent hires" description="Hire someone to start onboarding them." />
            : recentHires.map(emp => <ActiveRow key={emp.id} employee={emp} />)
          }
        </div>
      )}

      {innerTab === 'plans' && (
        loadingPlans ? <div className="flex justify-center p-20"><Spinner size="lg" /></div>
        : (plans || []).length === 0
          ? <EmptyState title="No onboarding plans" description="Create a template plan to standardise how new hires ramp up." />
          : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {plans.map(p => (
                <div key={p.id} className="card p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold text-gray-900">{p.name}</p>
                      <p className="text-xs text-gray-500 capitalize">{p.role?.replace('_',' ') || 'All roles'}</p>
                    </div>
                    <span className="text-xs text-gray-400">{p.tasks_count ?? 0} tasks</span>
                  </div>
                  {p.description && <p className="text-sm text-gray-600 mb-3 line-clamp-2">{p.description}</p>}
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                    <button onClick={() => setEditingPlan(p)} className="btn-secondary text-xs py-1 px-2">
                      <PencilSquareIcon className="w-3 h-3" /> Edit
                    </button>
                    <button onClick={() => setConfirmDeletePlan(p)} className="btn-secondary text-xs py-1 px-2 text-red-600">
                      <TrashIcon className="w-3 h-3" /> Archive
                    </button>
                    {p.is_active
                      ? <span className="ml-auto text-xs text-green-600 inline-flex items-center gap-1"><CheckCircleIcon className="w-3 h-3" /> Active</span>
                      : <span className="ml-auto text-xs text-gray-400">Archived</span>}
                  </div>
                </div>
              ))}
            </div>
          )
      )}

      <PlanEditor
        open={!!editingPlan}
        plan={editingPlan?.id ? (planDetail || editingPlan) : null}
        tasks={planDetail?.tasks || []}
        onClose={() => setEditingPlan(null)}
        onSavePlan={(data) => editingPlan?.id
          ? updatePlan.mutate({ id: editingPlan.id, data })
          : createPlan.mutate(data)}
        onAddTask={(data) => addTask.mutate({ planId: editingPlan.id, data })}
        onUpdateTask={(id, data) => updateTask.mutate({ id, data })}
        onDeleteTask={(id) => deleteTask.mutate(id)}
        savingPlan={createPlan.isPending || updatePlan.isPending}
      />

      <AssignModal
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        onSubmit={(v) => assign.mutate(v)}
        pending={assign.isPending}
        plans={plans}
        employees={employees}
      />

      <ConfirmModal
        open={!!confirmDeletePlan}
        onClose={() => setConfirmDeletePlan(null)}
        onConfirm={() => destroyPlan.mutate(confirmDeletePlan.id)}
        title="Archive plan?"
        message={`"${confirmDeletePlan?.name}" will be archived. Existing assignments remain.`}
        danger
      />
    </div>
  );
}
